import { motion } from "framer-motion";
import { ArrowLeft, Shield, Eye, Database, Cookie, Globe, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

export default function Privacy() {
  const lastUpdated = "April 20, 2026";

  const sections = [
    {
      icon: <Database className="text-blue-500" size={24} strokeWidth={2.5} />,
      title: "1. Information We Collect",
      content: "When you register on Info Payangadi, we collect personal information such as your name, email address, and profile picture (if you sign in using Google). We also collect non-personal data like browser type, device information, and usage statistics to improve our services."
    },
    {
      icon: <Eye className="text-emerald-500" size={24} strokeWidth={2.5} />,
      title: "2. How We Use Your Information",
      content: "We use the information we collect to create and manage your account, provide localized news and directory services, communicate with you regarding updates or support, and personalize your experience on the Info Payangadi platform."
    },
    {
      icon: <Cookie className="text-amber-500" size={24} strokeWidth={2.5} />,
      title: "3. Cookies and Tracking",
      content: "Our website uses 'cookies' to enhance user experience and analyze site traffic. Third-party vendors, including Google, use cookies to serve ads based on your prior visits to our website or other websites. You can choose to disable cookies through your browser settings."
    },
    {
      icon: <Globe className="text-purple-500" size={24} strokeWidth={2.5} />,
      title: "4. Third-Party Services & AdSense",
      content: "We use Google AdSense to display advertisements. Google's use of advertising cookies enables it and its partners to serve ads to our users based on their visit to our sites and/or other sites on the Internet. We also use Firebase for secure authentication and database management."
    },
    {
      icon: <Lock className="text-rose-500" size={24} strokeWidth={2.5} />,
      title: "5. Data Security",
      content: "We value your trust in providing us your Personal Information, thus we are striving to use commercially acceptable means of protecting it. We use industry-standard encryption (via Firebase Auth) to secure your passwords and login sessions."
    },
    {
      icon: <Shield className="text-slate-500" size={24} strokeWidth={2.5} />,
      title: "6. Your Privacy Rights",
      content: "You have the right to access, update, or delete the information we have on you. If you wish to remove your account and all associated data from Info Payangadi, you can do so from your profile settings or by contacting our support team."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Privacy Policy | Info Payangadi</title>
        <meta name="description" content="Privacy Policy and data handling guidelines for Info Payangadi." />
      </Helmet>

      <div className="min-h-[100dvh] w-full bg-[#F8FAFC] pb-24 relative overflow-hidden selection:bg-blue-200">
        
        {/* Background Glows */}
        <div className="absolute top-[-10%] right-[-10%] w-[400px] h-[400px] bg-emerald-400/10 rounded-full blur-[100px] pointer-events-none"></div>
        <div className="absolute top-[40%] left-[-10%] w-[300px] h-[300px] bg-blue-400/10 rounded-full blur-[100px] pointer-events-none"></div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-8 md:pt-12 relative z-10">
          
          {/* Back Button */}
          {/* <Link 
            to="/auth" 
            className="inline-flex items-center gap-2 text-slate-500 hover:text-emerald-600 font-bold transition-colors bg-white px-4 py-2 rounded-full shadow-sm border border-slate-100 mb-8"
          >
            <ArrowLeft size={18} strokeWidth={2.5} />
            <span>Back</span>
          </Link> */}

          {/* Header */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center justify-center p-4 bg-white text-emerald-600 rounded-2xl mb-4 shadow-[0_8px_30px_rgb(16,185,129,0.12)] border border-emerald-50">
              <Shield size={32} strokeWidth={2.5} />
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-slate-800 tracking-tight mb-4">
              Privacy <span className="text-emerald-600">Policy</span>
            </h1>
            <p className="text-slate-500 font-medium max-w-2xl mx-auto">
              Your privacy is critically important to us. This policy outlines how we collect, use, and protect your personal data when you use Info Payangadi.
            </p>
            <div className="mt-4 inline-block bg-slate-100 px-4 py-1.5 rounded-full border border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-widest">
              Last Updated: {lastUpdated}
            </div>
          </motion.div>

          {/* Content Cards */}
          <div className="space-y-6">
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 sm:p-8 rounded-[2rem] shadow-[0_8px_30px_rgb(0,0,0,0.03)] border border-slate-100 hover:shadow-[0_15px_40px_rgb(0,0,0,0.06)] transition-all duration-300"
              >
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-5 items-start">
                  <div className="bg-slate-50 p-3 rounded-2xl shrink-0 border border-slate-100">
                    {section.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-black text-slate-800 mb-3 tracking-tight">
                      {section.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed font-medium text-sm sm:text-base">
                      {section.content}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Contact Section */}
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mt-12 text-center bg-emerald-600 rounded-[2rem] p-8 sm:p-12 shadow-xl shadow-emerald-600/20 text-white"
          >
            <h3 className="text-2xl font-black mb-3">Data Privacy Inquiries</h3>
            <p className="text-emerald-100 font-medium mb-6 max-w-xl mx-auto">
              If you have any questions about how we handle your data or wish to request data deletion, please reach out to us.
            </p>
            <a 
              href="mailto:payangadilive@gmail.com" 
              className="inline-flex items-center justify-center bg-white text-emerald-600 font-black px-8 py-3.5 rounded-xl shadow-sm hover:bg-emerald-50 hover:scale-105 transition-all active:scale-95"
            >
              Email Privacy Team
            </a>
          </motion.div>

        </div>
      </div>
    </>
  );
}